import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getISOWeek } from "date-fns";

export interface MarketingEventStaff {
  id: string;
  staff_id: string;
  staff?: {
    first_name: string;
    last_name: string;
  } | null;
}

export interface MarketingEvent {
  id: string;
  title: string;
  description: string | null;
  event_type: string;
  event_date: string | null;
  year: number;
  week_number: number;
  staff_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  staff?: {
    first_name: string;
    last_name: string;
  } | null;
  event_staff?: MarketingEventStaff[];
}

export const useMarketingEvents = (year: number) => {
  const queryClient = useQueryClient();

  const eventsQuery = useQuery({
    queryKey: ["marketing-events", year],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketing_events" as any)
        .select("*, staff:crm_staff!marketing_events_staff_id_fkey(first_name, last_name)")
        .eq("year", year)
        .order("week_number", { ascending: true });

      if (error) throw error;
      const events = (data as any[]) as MarketingEvent[];

      // Fetch staff assignments for all events
      const eventIds = events.map(e => e.id);
      if (eventIds.length > 0) {
        const { data: staffData } = await supabase
          .from("marketing_event_staff" as any)
          .select("*, staff:crm_staff!marketing_event_staff_staff_id_fkey(first_name, last_name)")
          .in("event_id", eventIds);

        const staffByEvent: Record<string, MarketingEventStaff[]> = {};
        (staffData as any[] || []).forEach((s: any) => {
          if (!staffByEvent[s.event_id]) staffByEvent[s.event_id] = [];
          staffByEvent[s.event_id].push(s);
        });
        events.forEach(e => { e.event_staff = staffByEvent[e.id] || []; });
      }

      return events;
    },
  });

  const createEvent = useMutation({
    mutationFn: async (event: {
      title: string;
      description?: string;
      event_type: string;
      event_date?: string | null;
      year: number;
      week_number: number;
      staff_ids?: string[];
    }) => {
      const { staff_ids, ...eventData } = event;
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("marketing_events" as any)
        .insert({ ...eventData, created_by: user?.id } as any)
        .select()
        .single();
      if (error) throw error;

      // Insert staff assignments
      if (staff_ids && staff_ids.length > 0) {
        const { error: staffError } = await supabase
          .from("marketing_event_staff" as any)
          .insert(staff_ids.map(sid => ({ event_id: (data as any).id, staff_id: sid })) as any);
        if (staffError) throw staffError;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marketing-events", year] });
      toast.success("Event created successfully");
    },
    onError: (error: any) => {
      toast.error("Failed to create event: " + error.message);
    },
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, staff_ids, ...updates }: {
      id: string;
      title?: string;
      description?: string;
      event_type?: string;
      event_date?: string | null;
      year?: number;
      week_number?: number;
      staff_ids?: string[];
    }) => {
      const { data, error } = await supabase
        .from("marketing_events" as any)
        .update({ ...updates, updated_at: new Date().toISOString() } as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      // Replace staff assignments
      if (staff_ids !== undefined) {
        await supabase.from("marketing_event_staff" as any).delete().eq("event_id", id);
        if (staff_ids.length > 0) {
          const { error: staffError } = await supabase
            .from("marketing_event_staff" as any)
            .insert(staff_ids.map(sid => ({ event_id: id, staff_id: sid })) as any);
          if (staffError) throw staffError;
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marketing-events", year] });
      toast.success("Event updated successfully");
    },
    onError: (error: any) => {
      toast.error("Failed to update event: " + error.message);
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("marketing_events" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marketing-events", year] });
      toast.success("Event deleted successfully");
    },
    onError: (error: any) => {
      toast.error("Failed to delete event: " + error.message);
    },
  });

  return { eventsQuery, createEvent, updateEvent, deleteEvent };
};
